import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Play, CheckCircle2, ShieldCheck, Users, Home, CreditCard, Bike, BrainCircuit, MapPin } from 'lucide-react';
import { Section } from './ui/Section';
const serviceSlides = [{
  icon: ShieldCheck,
  title: 'Physical Verification',
  description: 'Every property is inspected by our Simba Fleet ambassadors on branded bicycles',
  color: 'from-emerald-500 to-green-600'
}, {
  icon: BrainCircuit,
  title: 'AI Roommate Matching',
  description: 'Smart algorithm pairs you with compatible roommates based on lifestyle and preferences',
  color: 'from-brand-primary to-purple-600'
}, {
  icon: CreditCard,
  title: 'Secure Escrow Payments',
  description: 'Your money is protected until you verify the property and move in',
  color: 'from-blue-500 to-indigo-600'
}, {
  icon: MapPin,
  title: 'Safety Monitoring',
  description: 'Real-time tracking and 24/7 emergency response for student safety',
  color: 'from-orange-500 to-red-600'
}];
export function ExplainerVideo() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  useEffect(() => {
    if (isPlaying) {
      const interval = setInterval(() => {
        setCurrentSlide(prev => (prev + 1) % serviceSlides.length);
      }, 3000);
      return () => clearInterval(interval);
    }
  }, [isPlaying]);
  const features = ['Physical verification by Simba Fleet', 'AI-powered roommate matching', 'Secure escrow payment system', '24/7 safety monitoring'];
  return <Section className="bg-gradient-to-br from-brand-dark via-brand-dark-secondary to-brand-dark text-white overflow-hidden relative" data-id="element-45" data-mp="1soxiroh6359i">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5" style={{
      backgroundImage: 'radial-gradient(circle, #b583c9 1px, transparent 1px)',
      backgroundSize: '40px 40px'
    }} data-id="element-46" data-mp="14xjq1hf3t7o0"></div>

      <div className="relative z-10 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center" data-id="element-47" data-mp="351cmjdeaghh6">
        {/* Left: Text Content */}
        <motion.div initial={{
        opacity: 0,
        x: -20
      }} whileInView={{
        opacity: 1,
        x: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-48" data-mp="39tdtcjsotfzt">
          <div className="inline-block px-3 py-1 bg-brand-primary/20 text-brand-primary rounded-full text-sm font-medium mb-6" data-id="element-49" data-mp="2m3jiu8vuc3iq">
            How It Works
          </div>
          <h2 className="text-4xl md:text-5xl font-bold mb-6 leading-tight" data-id="element-50" data-mp="2v3y63dsd7sov">
            See CampusAparte <br data-id="element-51" data-mp="6c5dg5hisq7q" />
            in <span className="text-brand-primary" data-id="element-52" data-mp="6akjagz4b9ae">Action</span>
          </h2>
          <p className="text-xl text-slate-300 mb-8 leading-relaxed" data-id="element-53" data-mp="acxqzaxfdh9t">
            Watch how we're revolutionizing student housing across Africa with
            verified properties, smart matching, and secure payments.
          </p>

          <ul className="space-y-4 mb-8" data-id="element-54" data-mp="1quxzhdk6nsqb">
            {features.map((feature, idx) => <motion.li key={idx} initial={{
            opacity: 0,
            x: -20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.4,
            delay: idx * 0.1
          }} className="flex items-center gap-3 text-lg" data-id="element-55" data-mp="1m1wkz6nw1v79" data-mpi={idx}>
                <div className="w-6 h-6 rounded-full bg-brand-primary/20 flex items-center justify-center flex-shrink-0" data-id="element-56" data-mp="y6tsmopvl9m1">
                  <CheckCircle2 className="w-4 h-4 text-brand-primary" data-id="element-57" data-mp="2sj0h2p7s5mwa" />
                </div>
                <span className="text-slate-200" data-id="element-58" data-mp="2sh5vr9sljsro">{feature}</span>
              </motion.li>)}
          </ul>

          <div className="flex gap-4" data-id="element-59" data-mp="1u6xjxeozoleb">
            <button className="px-6 py-3 bg-brand-primary hover:bg-brand-light text-white font-semibold rounded-lg transition-colors" data-id="element-60" data-mp="1u5wblhzcyd0a">
              Get Started
            </button>
            <button className="px-6 py-3 border border-brand-primary/30 hover:bg-brand-dark-secondary text-white font-semibold rounded-lg transition-colors" data-id="element-61" data-mp="1l5pwxsrxb6zj">
              Learn More
            </button>
          </div>
        </motion.div>

        {/* Right: Custom Video Interface */}
        <motion.div initial={{
        opacity: 0,
        x: 20
      }} whileInView={{
        opacity: 1,
        x: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="relative" data-id="element-62" data-mp="xhggl6dhbbfs">
          <div className="relative rounded-2xl overflow-hidden shadow-2xl shadow-brand-primary/20 border border-brand-primary/20 bg-gradient-to-br from-brand-dark-secondary to-brand-dark" data-id="element-63" data-mp="shaj39kj2ups">
            {/* Custom Video Player */}
            <div className="aspect-video relative" data-id="element-64" data-mp="iu829wovipzl">
              {!isPlaying ? <div className="relative w-full h-full flex items-center justify-center cursor-pointer group" onClick={() => setIsPlaying(true)} data-id="element-65" data-mp="is52wm1tzki7">
                  {/* Background Image */}
                  <div className="absolute inset-0" data-id="element-66" data-mp="mwl78ivsqjvb">
                    <img src="https://images.unsplash.com/photo-1523050854058-8df90110c9f1?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80" alt="Students" className="w-full h-full object-cover opacity-40" data-id="element-67" data-mp="mtqlmo11qt0b" />
                    <div className="absolute inset-0 bg-gradient-to-br from-brand-primary/40 to-brand-dark/80" data-id="element-68" data-mp="1alnpnsy98gqv"></div>
                  </div>

                  {/* Animated Icons */}
                  <div className="absolute inset-0" data-id="element-69" data-mp="1dojzcxa8lsgo">
                    <motion.div animate={{
                  y: [0, -10, 0],
                  opacity: [0.5, 1, 0.5]
                }} transition={{
                  duration: 2,
                  repeat: Infinity
                }} className="absolute top-1/4 left-1/4 w-12 h-12 bg-emerald-500/20 rounded-full flex items-center justify-center" data-id="element-70" data-mp="1iltw6xzyh6ej">
                      <ShieldCheck className="w-6 h-6 text-emerald-400" data-id="element-71" data-mp="21eebfcmqfczv" />
                    </motion.div>
                    <motion.div animate={{
                  y: [0, 10, 0],
                  opacity: [0.5, 1, 0.5]
                }} transition={{
                  duration: 2.5,
                  repeat: Infinity,
                  delay: 0.5
                }} className="absolute top-1/3 right-1/4 w-12 h-12 bg-brand-primary/20 rounded-full flex items-center justify-center" data-id="element-72" data-mp="2bar3y76jjkt1">
                      <Users className="w-6 h-6 text-brand-primary" data-id="element-73" data-mp="2lefw0bwo08h4" />
                    </motion.div>
                    <motion.div animate={{
                  y: [0, -10, 0],
                  opacity: [0.5, 1, 0.5]
                }} transition={{
                  duration: 2.2,
                  repeat: Infinity,
                  delay: 1
                }} className="absolute bottom-1/4 left-1/3 w-12 h-12 bg-blue-500/20 rounded-full flex items-center justify-center" data-id="element-74" data-mp="2gfutg5txxcpi">
                      <Home className="w-6 h-6 text-blue-400" data-id="element-75" data-mp="1sqad7up68tjx" />
                    </motion.div>
                  </div>

                  {/* Play Button */}
                  <motion.button whileHover={{
                scale: 1.1
              }} whileTap={{
                scale: 0.95
              }} className="relative z-10 w-20 h-20 bg-brand-primary hover:bg-brand-light rounded-full flex items-center justify-center shadow-lg shadow-brand-primary/50 transition-colors" data-id="element-76" data-mp="1nta6dhkhzmrr">
                    <Play className="w-8 h-8 text-white ml-1" fill="currentColor" data-id="element-77" data-mp="aek2ugces4eb" />
                  </motion.button>

                  {/* Overlay Text */}
                  <div className="absolute bottom-6 left-6 right-6 z-10" data-id="element-78" data-mp="nfrdqlxz1o8">
                    <h3 className="text-2xl font-bold text-white mb-2" data-id="element-79" data-mp="5naj1wdjjvac">
                      Watch Our Story
                    </h3>
                    <p className="text-slate-200 text-sm" data-id="element-80" data-mp="tccxywgsja15">
                      See how we're making student housing safer across Africa
                    </p>
                  </div>
                </div> : <div className="relative w-full h-full bg-gradient-to-br from-brand-dark-secondary to-brand-dark p-8 flex flex-col items-center justify-center" data-id="element-81" data-mp="2isfg231clmgj">
                  {/* Animated Service Showcase */}
                  <AnimatePresence mode="wait" data-id="element-82" data-mp="2slwb1ad817ls">
                    <motion.div key={currentSlide} initial={{
                  opacity: 0,
                  scale: 0.8,
                  rotateY: -90
                }} animate={{
                  opacity: 1,
                  scale: 1,
                  rotateY: 0
                }} exit={{
                  opacity: 0,
                  scale: 0.8,
                  rotateY: 90
                }} transition={{
                  duration: 0.6
                }} className="text-center" data-id="element-83" data-mp="2nm1n9foqsy4y">
                      <motion.div className={`w-24 h-24 mx-auto mb-6 rounded-full bg-gradient-to-br ${serviceSlides[currentSlide].color} flex items-center justify-center shadow-2xl`} animate={{
                    rotate: [0, 360],
                    scale: [1, 1.1, 1]
                  }} transition={{
                    rotate: {
                      duration: 20,
                      repeat: Infinity,
                      ease: 'linear'
                    },
                    scale: {
                      duration: 2,
                      repeat: Infinity
                    }
                  }} data-id="element-84" data-mp="30xuv9gwnfass">
                        {(() => {
                      const Icon = serviceSlides[currentSlide].icon;
                      return <Icon className="w-12 h-12 text-white" data-id="element-85" data-mp="2ca8ft333ybh" />;
                    })()}
                      </motion.div>

                      <h3 className="text-3xl font-bold text-white mb-4" data-id="element-86" data-mp="ew727p5bdfls">
                        {serviceSlides[currentSlide].title}
                      </h3>
                      <p className="text-lg text-slate-300 max-w-md mx-auto" data-id="element-87" data-mp="jpjq1x265idh">
                        {serviceSlides[currentSlide].description}
                      </p>
                    </motion.div>
                  </AnimatePresence>

                  {/* Progress Dots */}
                  <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex gap-2" data-id="element-88" data-mp="3s3sbml69x8w5">
                    {serviceSlides.map((_, idx) => <button key={idx} onClick={() => setCurrentSlide(idx)} className={`w-2 h-2 rounded-full transition-all ${currentSlide === idx ? 'bg-brand-primary w-8' : 'bg-slate-600 hover:bg-slate-500'}`} data-id="element-89" data-mp="4yay3hasnkx6" data-mpi={idx} />)}
                  </div>

                  {/* Close Button */}
                  <button onClick={() => setIsPlaying(false)} className="absolute top-4 right-4 px-4 py-2 bg-white/10 hover:bg-white/20 rounded-lg text-white text-sm font-medium transition-colors" data-id="element-90" data-mp="38fj167e9utzx">
                    Close
                  </button>
                </div>}
            </div>

            {/* Stats Overlay */}
            {!isPlaying && <div className="absolute -bottom-6 left-6 right-6 bg-white rounded-xl shadow-xl p-4 grid grid-cols-3 gap-4" data-id="element-91" data-mp="2ppsvgp8z0nt8">
                <div className="text-center" data-id="element-92" data-mp="33hqw0bom9bds">
                  <div className="text-2xl font-bold text-brand-dark" data-id="element-93" data-mp="2yppqd8lyeiwq">
                    5,000+
                  </div>
                  <div className="text-xs text-slate-500" data-id="element-94" data-mp="2az3grcwawqkm">Students</div>
                </div>
                <div className="text-center border-x border-slate-200" data-id="element-95" data-mp="261k4gij5ynvb">
                  <div className="text-2xl font-bold text-brand-dark" data-id="element-96" data-mp="lirifagi3lcq">850+</div>
                  <div className="text-xs text-slate-500" data-id="element-97" data-mp="lgn4s0ssv7z9">Properties</div>
                </div>
                <div className="text-center" data-id="element-98" data-mp="qbkl7ssesjm6">
                  <div className="text-2xl font-bold text-brand-dark" data-id="element-99" data-mp="cil4jngw4jd1">120+</div>
                  <div className="text-xs text-slate-500" data-id="element-100" data-mp="hgwh45ykl7yy">Ambassadors</div>
                </div>
              </div>}
          </div>
        </motion.div>
      </div>
    </Section>;
}